import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, CheckCircle2, Loader2, MapPin, QrCode, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import SiteNav from "@/components/landing/Nav";
import { api } from "@/lib/api";
import { formatTimeRange12 } from "@/lib/time";

const EASE = [0.16, 1, 0.3, 1];

export default function TicketPage() {
  const { code } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/tickets/${code}`)
      .then(({ data }) => setTicket(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [code]);

  const ev = ticket?.event || {};

  return (
    <div data-testid="ticket-page" className="min-h-screen bg-ink text-foreground">
      <Seo
        title={ev.title ? `Your Ticket — ${ev.title}` : "Your Ticket — AB Hunter Consulting USA"}
        siteName="AB Hunter Consulting USA"
        description="Your event check-in QR ticket."
        noindex
      />
      <SiteNav />
      <div className="lg:pl-64">
        <main className="hero-glow grain relative mx-auto max-w-2xl px-5 pb-24 pt-24 sm:px-8 lg:pt-16">
          <Link to="/" data-testid="ticket-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-gold">
            <ArrowLeft className="h-4 w-4" /> Back to AB Hunter
          </Link>

          {loading ? (
            <div className="flex justify-center py-32" data-testid="ticket-loading"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
          ) : notFound ? (
            <div className="py-32 text-center" data-testid="ticket-not-found">
              <QrCode className="mx-auto h-12 w-12 text-gold/30" />
              <p className="mt-4 font-display text-2xl">Ticket not found</p>
              <p className="mt-2 text-sm text-muted-foreground">Double-check the link from your confirmation email, or call 225.603.4116.</p>
              <Button asChild className="mt-7 rounded-full bg-gold px-8 font-semibold text-ink hover:bg-gold-hover"><Link to="/">Return home</Link></Button>
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="mt-8">
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Your ticket</span>
              <h1 className="mt-3 font-display text-3xl font-semibold leading-tight tracking-tight">{ev.title}</h1>
              <div className="mt-5 space-y-2.5 text-sm text-foreground/85">
                <p className="flex items-center gap-3"><CalendarDays className="h-4 w-4 shrink-0 text-gold" /> {ev.date_label}{ev.start_time ? ` · ${formatTimeRange12(ev.start_time, ev.end_time)}` : ""}</p>
                <p className="flex items-center gap-3"><MapPin className="h-4 w-4 shrink-0 text-gold" /> {ev.location}</p>
              </div>

              <div className="mt-8 rounded-3xl border border-gold/25 bg-ink-card/70 p-7 text-center sm:p-10" data-testid="ticket-card">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Show this at the door</p>
                <p className="mt-2 font-display text-xl font-semibold" data-testid="ticket-holder-name">{ticket.holder?.name || "Guest"}</p>
                <div className="mx-auto mt-6 w-fit rounded-2xl bg-white p-4 shadow-[0_0_50px_rgba(212,175,55,0.15)]">
                  <img src={`/api/tickets/${code}/qr.png`} alt={`QR check-in code for ${ticket.holder?.name || "guest"}`}
                    data-testid="ticket-qr-image" className="h-56 w-56 sm:h-64 sm:w-64" />
                </div>
                {ticket.holder?.checked_in ? (
                  <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-1.5 text-sm font-semibold text-emerald-300" data-testid="ticket-status">
                    <CheckCircle2 className="h-4 w-4" /> Checked in {ticket.holder.checked_in_at ? new Date(ticket.holder.checked_in_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                  </p>
                ) : (
                  <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 text-sm font-semibold text-gold" data-testid="ticket-status">
                    <QrCode className="h-4 w-4" /> Ready to scan
                  </p>
                )}
              </div>

              {ticket.party?.length > 1 && (
                <div className="mt-8 rounded-3xl border border-gold/15 bg-ink-card/60 p-7" data-testid="ticket-party">
                  <p className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                    <Users className="h-4 w-4" /> Everyone registered with you
                  </p>
                  <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
                    {ticket.party.map((p) => (
                      <Link key={p.code} to={`/ticket/${p.code}`} data-testid={`ticket-party-${p.code}`}
                        className={`rounded-2xl border p-3.5 text-center transition-all hover:border-gold/50 ${p.code === code ? "border-gold/50 bg-gold/10" : "border-gold/15 bg-ink"}`}>
                        <img src={`/api/tickets/${p.code}/qr.png`} alt={`QR code for ${p.name || "guest"}`} className="mx-auto h-20 w-20 rounded-lg bg-white p-1.5" />
                        <p className="mt-2 truncate text-xs font-semibold">{p.name || "Guest"}</p>
                        <p className={`mt-0.5 text-[10px] font-medium ${p.checked_in ? "text-emerald-300" : "text-muted-foreground"}`}>
                          {p.checked_in ? "Checked in" : "Not yet"}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </main>
      </div>
    </div>
  );
}
